import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Shield,
  Download,
  Search,
  AlertTriangle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Server,
  RefreshCw,
  Globe,
  Bug,
  Wifi,
  Skull,
  Zap,
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { IS_PRERENDER } from '@/lib/prerender';

const IPListPage = () => {
  const { language } = useLanguage();
  const [ipList, setIpList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [threatFilter, setThreatFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [lastUpdated, setLastUpdated] = useState(null);

  const translations = {
    tr: {
      title: 'Zararlı IP Adresleri Listesi',
      description: 'Threat.live tarafından tespit edilen zararlı IP adresleri',
      lastUpdate: 'Son Güncelleme',
      totalRecords: 'Toplam Kayıt',
      search: 'IP, ASN veya Kuruluş Ara...',
      ipAddress: 'IP Adresi',
      threatType: 'Tehdit Türü',
      asNumber: 'AS Numarası',
      organization: 'Kuruluş',
      country: 'Ülke',
      download: 'IP Listesi İndir',
      downloadRsc: 'MikroTik RSC İndir',
      showing: 'Gösterilen',
      of: '/',
      entries: 'kayıt',
      previous: 'Önceki',
      next: 'Sonraki',
      loading: 'Yükleniyor...',
      noResults: 'Sonuç bulunamadı',
      perPage: 'Sayfa başına',
      allThreats: 'Tüm Tehditler',
      unknown: 'Bilinmiyor',
    },
    en: {
      title: 'List of Malicious IP Addresses',
      description: 'Malicious IP addresses detected by Threat.live',
      lastUpdate: 'Last Update',
      totalRecords: 'Total Records',
      search: 'Search IP, ASN or Organization...',
      ipAddress: 'IP Address',
      threatType: 'Threat Type',
      asNumber: 'AS Number',
      organization: 'Organization',
      country: 'Country',
      download: 'Download IP List',
      downloadRsc: 'Download MikroTik RSC',
      showing: 'Showing',
      of: 'of',
      entries: 'entries',
      previous: 'Previous',
      next: 'Next',
      loading: 'Loading...',
      noResults: 'No results found',
      perPage: 'Per page',
      allThreats: 'All Threats',
      unknown: 'Unknown',
    },
  };

  const txt = translations[language] || translations.en;

  // Threat type SOC-style badge classes + icons
  const getThreatInfo = (threatType) => {
    const threats = {
      'Brute Force': { cls: 'soc-badge soc-badge--red', icon: Skull },
      'Bruteforce':  { cls: 'soc-badge soc-badge--red', icon: Skull },
      'Ağ Tarama':   { cls: 'soc-badge soc-badge--amber', icon: Wifi },
      'Network Scan':{ cls: 'soc-badge soc-badge--amber', icon: Wifi },
      'Web Tarama':  { cls: 'soc-badge soc-badge--neutral !text-cyan-400 !border-cyan-400/40 !bg-cyan-400/10', icon: Globe },
      'Web Scan':    { cls: 'soc-badge soc-badge--neutral !text-cyan-400 !border-cyan-400/40 !bg-cyan-400/10', icon: Globe },
      'DDoS':        { cls: 'soc-badge soc-badge--neutral !text-violet-400 !border-violet-400/40 !bg-violet-400/10', icon: Zap },
      'Diğer':       { cls: 'soc-badge soc-badge--neutral', icon: Bug },
      'Other':       { cls: 'soc-badge soc-badge--neutral', icon: Bug },
    };
    return threats[threatType] || { cls: 'soc-badge soc-badge--neutral', icon: AlertTriangle };
  };

  const getFlagUrl = (isoCode) => {
    if (!isoCode) return null;
    return `https://flagcdn.com/w20/${isoCode.toLowerCase()}.png`;
  };

  const fetchIPList = useCallback(async () => {
    if (IS_PRERENDER) return;
    setIsLoading(true);
    try {
      const response = await fetch('https://api.threat.live/cache/ip_list_cache.json');
      if (response.ok) {
        const data = await response.json();
        setIpList(data);
        setFilteredList(data);
        setLastUpdated(new Date());
      }
    } catch (err) {
      console.error('Error fetching IP list:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIPList();
  }, [fetchIPList]);

  useEffect(() => {
    let filtered = ipList;

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.ip_address?.toLowerCase().includes(search) ||
        item.org?.toLowerCase().includes(search) ||
        item.asn?.toString().includes(search) ||
        item.country?.toLowerCase().includes(search)
      );
    }

    if (threatFilter !== 'all') {
      filtered = filtered.filter(item => item.category === threatFilter);
    }

    filtered = [...filtered].sort((a, b) => {
      const aComplete = Boolean(a.asn && a.country && a.org);
      const bComplete = Boolean(b.asn && b.country && b.org);
      if (aComplete && !bComplete) return -1;
      if (!aComplete && bComplete) return 1;
      return 0;
    });

    setFilteredList(filtered);
    setCurrentPage(1);
  }, [searchTerm, threatFilter, ipList]);

  const threatTypes = [...new Set(ipList.map(item => item.category).filter(Boolean))];

  const totalPages = Math.ceil(filteredList.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredList.slice(startIndex, endIndex);

  const handleDownload = () => {
    window.open('https://list.threat.live/', '_blank');
  };

  const handleDownloadRsc = () => {
    const rscContent = ipList.map(item =>
      `/ip firewall address-list add list=Threat-Live address=${item.ip_address} comment="${item.category || 'Blocked'}"`
    ).join('\n');

    const blob = new Blob([rscContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'threat-live.rsc';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="ip-list-page">
      <SEO page="ipList" />
      <Header />

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Section meta */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-primary">
              // INTEL.FEED · R-02
            </span>
            <span className="flex-1 h-px bg-border" />
            <span className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground">
              <span className="status-dot" />
              FEED LIVE
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2 flex items-center gap-3">
            <div className="p-2 border border-primary/40 bg-primary/10 sharp">
              <AlertTriangle className="h-6 w-6 text-primary" strokeWidth={2.25} />
            </div>
            {txt.title}
          </h1>
          <p className="text-muted-foreground md:ml-[60px]">{txt.description}</p>
        </div>

        {/* Stats & Actions */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="border border-border bg-card corner-ticks">
            <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/30">
              <div className="flex items-center gap-2">
                <Shield className="h-3.5 w-3.5 text-primary" strokeWidth={2.25} />
                <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
                  {txt.totalRecords}
                </span>
              </div>
              <span className="flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-[0.18em] text-primary">
                <span className="status-dot" />
                LIVE
              </span>
            </div>
            <div className="p-4">
              <p className="text-2xl font-mono tabular font-bold text-primary">{ipList.length.toLocaleString()}</p>
              <p className="mt-1 text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground">
                // {language === 'tr' ? 'Aktif blocklist' : 'Active blocklist'}
              </p>
            </div>
          </div>

          <div className="border border-border bg-card">
            <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/30">
              <div className="flex items-center gap-2">
                <RefreshCw className="h-3.5 w-3.5 text-cyan-400" strokeWidth={2.25} />
                <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
                  {txt.lastUpdate}
                </span>
              </div>
            </div>
            <div className="p-4">
              <p className="text-sm font-mono text-foreground">
                {lastUpdated ? lastUpdated.toLocaleString() : '—'}
              </p>
              <p className="mt-1 text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground">
                // {language === 'tr' ? 'Snapshot zaman damgası' : 'Snapshot timestamp'}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleDownload}
            className="text-left border border-border bg-card hover:border-primary/50 hover:bg-muted/30 transition-colors"
          >
            <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/30">
              <div className="flex items-center gap-2">
                <Download className="h-3.5 w-3.5 text-primary" strokeWidth={2.25} />
                <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
                  TXT EXPORT
                </span>
              </div>
              <span className="soc-badge soc-badge--neutral !py-[2px] !px-1.5 !text-[9px]">.txt</span>
            </div>
            <div className="p-4">
              <p className="text-sm font-semibold text-foreground">{txt.download}</p>
              <p className="mt-1 text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground">
                // list.threat.live
              </p>
            </div>
          </button>

          <button
            type="button"
            onClick={handleDownloadRsc}
            className="text-left border border-border bg-card hover:border-primary/50 hover:bg-muted/30 transition-colors"
          >
            <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/30">
              <div className="flex items-center gap-2">
                <Server className="h-3.5 w-3.5 text-amber-400" strokeWidth={2.25} />
                <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
                  MIKROTIK EXPORT
                </span>
              </div>
              <span className="soc-badge soc-badge--neutral !py-[2px] !px-1.5 !text-[9px]">.rsc</span>
            </div>
            <div className="p-4">
              <p className="text-sm font-semibold text-foreground">{txt.downloadRsc}</p>
              <p className="mt-1 text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground">
                // /ip firewall address-list
              </p>
            </div>
          </button>
        </div>

        {/* Search & Filter panel */}
        <div className="border border-border bg-card mb-6">
          <div className="flex items-center justify-between px-5 py-2 border-b border-border bg-muted/30">
            <div className="flex items-center gap-2">
              <Search className="h-3.5 w-3.5 text-primary" strokeWidth={2.25} />
              <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-foreground">
                {language === 'tr' ? 'SORGU' : 'QUERY'}
              </span>
            </div>
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
              // FILTER
            </span>
          </div>
          <div className="p-4">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              <div className="relative flex-1 w-full sm:max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={txt.search}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 sharp font-mono text-xs"
                  data-testid="ip-search"
                />
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <Select value={threatFilter} onValueChange={setThreatFilter}>
                  <SelectTrigger className="w-40 sharp font-mono text-xs">
                    <SelectValue placeholder={txt.allThreats} />
                  </SelectTrigger>
                  <SelectContent className="sharp font-mono">
                    <SelectItem value="all">{txt.allThreats}</SelectItem>
                    {threatTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground">{txt.perPage}:</span>
                  <Select value={itemsPerPage.toString()} onValueChange={(v) => { setItemsPerPage(Number(v)); setCurrentPage(1); }}>
                    <SelectTrigger className="w-20 sharp font-mono text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="sharp font-mono">
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Records table panel */}
        <div className="border border-border bg-card">
          <div className="flex items-center justify-between px-5 py-2 border-b border-border bg-muted/30">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5 text-primary" strokeWidth={2.25} />
              <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-foreground">
                {language === 'tr' ? 'TEHDİT KAYITLARI' : 'THREAT RECORDS'}
              </span>
            </div>
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground tabular">
              {filteredList.length.toLocaleString()} {txt.entries}
            </span>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="ml-2 text-xs font-mono uppercase tracking-wider text-muted-foreground">{txt.loading}</span>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-b border-border">
                      <TableHead className="w-16 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">#</TableHead>
                      <TableHead className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{txt.ipAddress}</TableHead>
                      <TableHead className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{txt.threatType}</TableHead>
                      <TableHead className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{txt.asNumber}</TableHead>
                      <TableHead className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{txt.organization}</TableHead>
                      <TableHead className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{txt.country}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currentItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-10 text-muted-foreground font-mono text-xs uppercase tracking-wider">
                          {txt.noResults}
                        </TableCell>
                      </TableRow>
                    ) : (
                      currentItems.map((item, index) => {
                        const info = getThreatInfo(item.category);
                        const ThreatIcon = info.icon;
                        return (
                          <TableRow key={index} className="hover:bg-muted/40 border-b border-border/60">
                            <TableCell className="font-mono text-muted-foreground text-xs tabular">
                              {String(startIndex + index + 1).padStart(4, '0')}
                            </TableCell>
                            <TableCell className="font-mono font-semibold text-foreground">
                              {item.ip_address}
                            </TableCell>
                            <TableCell>
                              <span className={info.cls}>
                                <ThreatIcon className="h-3 w-3" />
                                {item.category || txt.unknown}
                              </span>
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {item.asn ? (
                                <span className="soc-badge soc-badge--neutral !py-[2px] !px-1.5 !text-[9px]">
                                  AS{item.asn}
                                </span>
                              ) : '—'}
                            </TableCell>
                            <TableCell className="text-sm max-w-[200px] truncate text-muted-foreground" title={item.org}>
                              {item.org || '—'}
                            </TableCell>
                            <TableCell>
                              {item.country ? (
                                <div className="flex items-center gap-2">
                                  {item.iso_code && (
                                    <img
                                      src={getFlagUrl(item.iso_code)}
                                      alt={item.country}
                                      className="w-5 h-[14px] object-cover border border-border/60"
                                      onError={(e) => e.target.style.display = 'none'}
                                    />
                                  )}
                                  <span className="text-sm">{item.country}</span>
                                </div>
                              ) : '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-border bg-muted/20">
                <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-muted-foreground">
                  {txt.showing} <span className="text-foreground tabular">{startIndex + 1}-{Math.min(endIndex, filteredList.length)}</span> {txt.of} <span className="text-foreground tabular">{filteredList.length.toLocaleString()}</span> {txt.entries}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="sharp font-mono text-[10px] uppercase tracking-wider"
                  >
                    <ChevronLeft className="h-3.5 w-3.5 mr-1" />
                    {txt.previous}
                  </Button>
                  <div className="flex items-center gap-1">
                    {[...Array(Math.min(5, totalPages))].map((_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      const active = currentPage === pageNum;
                      return (
                        <Button
                          key={i}
                          variant={active ? 'default' : 'outline'}
                          size="sm"
                          className={`w-8 h-8 p-0 sharp font-mono text-xs tabular ${active ? 'bg-primary text-primary-foreground border-primary' : ''}`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="sharp font-mono text-[10px] uppercase tracking-wider"
                  >
                    {txt.next}
                    <ChevronRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default IPListPage;
